import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft, Handshake, MapPin, Phone, AlertCircle, Loader2, ChevronDown, Info, LogIn
} from 'lucide-react';
import { api, municipiosApi } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';

const TIPOS = [
  { value: 'feira', label: 'Feira (gastronomia, artesanato, orgânicos...)' },
  { value: 'cooperativa', label: 'Cooperativa' },
  { value: 'associacao', label: 'Associação de produtores/comerciantes' },
  { value: 'grupo', label: 'Grupo (artesãos, produtores, cultural...)' },
  { value: 'rede', label: 'Rede de apoio/economia solidária' },
];

const FormField = ({ label, required, error, hint, children }) => (
  <div className="mb-5">
    <label className="block text-sm font-semibold text-gray-800 mb-1.5">
      {label} {required && <span className="text-red-500">*</span>}
    </label>
    {children}
    {hint && !error && <p className="text-xs text-gray-500 mt-1">{hint}</p>}
    {error && (
      <p className="text-xs text-red-500 mt-1 flex items-center gap-1">
        <AlertCircle className="w-3 h-3" /> {error}
      </p>
    )}
  </div>
);

export default function CadastrarColetivo() {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const [municipios, setMunicipios] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState({});
  const [form, setForm] = useState({
    nome: '',
    tipo: '',
    descricao: '',
    municipio_id: '',
    bairro: '',
    responsavel_contato: '',
    instagram: '',
    facebook: '',
  });

  useEffect(() => {
    municipiosApi.list().then((data) => {
      const list = Array.isArray(data) ? data : (data?.items || []);
      setMunicipios(list);
      if (list.length === 1) setForm(f => ({ ...f, municipio_id: list[0].id }));
    }).catch(() => {});
  }, []);

  const update = (k, v) => {
    setForm(f => ({ ...f, [k]: v }));
    if (errors[k]) setErrors(e => ({ ...e, [k]: null }));
  };

  const validate = () => {
    const e = {};
    if (!form.nome || form.nome.trim().length < 3) e.nome = 'Nome deve ter pelo menos 3 caracteres';
    if (!form.tipo) e.tipo = 'Selecione o tipo do coletivo';
    if (!form.descricao || form.descricao.trim().length < 10) e.descricao = 'Descreva o coletivo (mínimo 10 caracteres)';
    if (!form.municipio_id) e.municipio_id = 'Selecione o município';
    if (!form.responsavel_contato || form.responsavel_contato.replace(/\D/g, '').length < 10) {
      e.responsavel_contato = 'Informe um telefone/WhatsApp de contato válido';
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async (ev) => {
    ev.preventDefault();
    if (!validate()) {
      toast.error('Corrija os campos destacados');
      return;
    }
    setSubmitting(true);
    try {
      await api.post('/v2/coletivos/solicitar', {
        ...form,
        nome: form.nome.trim(),
        descricao: form.descricao.trim(),
      });
      toast.success('Solicitação enviada para análise da gestão municipal! Você será avisado por e-mail.');
      navigate('/responsavel/coletivos');
    } catch (error) {
      const d = error.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : 'Erro ao enviar a solicitação');
    } finally {
      setSubmitting(false);
    }
  };

  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-purple-600" />
      </div>
    );
  }

  if (!user) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4" data-testid="coletivo-login-required">
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-8 max-w-md text-center">
          <div className="w-14 h-14 rounded-2xl bg-purple-100 flex items-center justify-center mx-auto mb-4">
            <Handshake className="w-7 h-7 text-purple-600" />
          </div>
          <h1 className="text-xl font-bold text-gray-900 mb-2">Faça login para continuar</h1>
          <p className="text-gray-600 text-sm mb-6">
            Para solicitar o cadastro de um coletivo (feira, cooperativa, grupo de artesãos...),
            você precisa estar logado. O cadastro passa por análise da gestão municipal.
          </p>
          <button
            onClick={() => navigate('/perfil')}
            className="w-full py-3 bg-purple-600 text-white rounded-xl font-semibold hover:bg-purple-700 transition-colors flex items-center justify-center gap-2"
            data-testid="coletivo-login-button"
          >
            <LogIn className="w-4 h-4" />
            Fazer Login
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pb-16" data-testid="cadastrar-coletivo-page">
      <div className="bg-gradient-to-r from-purple-700 to-purple-500 text-white">
        <div className="max-w-2xl mx-auto px-4 py-8">
          <button onClick={() => navigate(-1)} className="flex items-center gap-1 text-white/80 hover:text-white text-sm mb-4">
            <ArrowLeft className="w-4 h-4" /> Voltar
          </button>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Handshake className="w-7 h-7" />
            Cadastrar Coletivo
          </h1>
          <p className="text-white/90 text-sm mt-2">
            Feiras, cooperativas, associações, grupos de artesãos e produtores.
            A solicitação passa por análise da gestão municipal — após aprovação,
            você gerencia membros e eventos do coletivo.
          </p>
        </div>
      </div>

      <div className="max-w-2xl mx-auto px-4 -mt-4">
        <form onSubmit={handleSubmit} className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 md:p-8">
          <div className="flex gap-3 p-4 bg-purple-50 rounded-xl mb-6">
            <Info className="w-5 h-5 text-purple-600 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-purple-800">
              <p className="font-medium">É uma ONG ou projeto social?</p>
              <p className="text-purple-600">
                Organizações sociais têm cadastro próprio em{' '}
                <button type="button" onClick={() => navigate('/social/cadastrar-organizacao')} className="underline font-semibold">
                  Cadastrar Organização Social
                </button>. Este formulário é para coletivos de empreendedores.
              </p>
            </div>
          </div>

          <FormField label="Nome do coletivo" required error={errors.nome}>
            <input
              type="text"
              value={form.nome}
              onChange={(e) => update('nome', e.target.value)}
              placeholder="Ex: Feira de Artesanato da Praça Central"
              maxLength={120}
              className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:ring-2 focus:ring-purple-500/20 focus:border-purple-500"
              data-testid="coletivo-nome-input"
            />
          </FormField>

          <FormField label="Tipo" required error={errors.tipo}>
            <div className="relative">
              <select
                value={form.tipo}
                onChange={(e) => update('tipo', e.target.value)}
                className="w-full px-4 py-3 rounded-xl border border-gray-200 bg-white appearance-none focus:ring-2 focus:ring-purple-500/20 focus:border-purple-500"
                data-testid="coletivo-tipo-select"
              >
                <option value="">Selecione...</option>
                {TIPOS.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>
              <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
            </div>
          </FormField>

          <FormField label="Descrição" required error={errors.descricao} hint={`${form.descricao.length}/500 — o que o coletivo faz, quem participa, onde e quando acontece`}>
            <textarea
              value={form.descricao}
              onChange={(e) => update('descricao', e.target.value)}
              rows={4}
              maxLength={500}
              placeholder="Ex: Grupo de 15 artesãs que produz e vende peças em crochê e cerâmica na feira de domingo..."
              className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:ring-2 focus:ring-purple-500/20 focus:border-purple-500 resize-none"
              data-testid="coletivo-descricao-input"
            />
          </FormField>

          <FormField label="Município" required error={errors.municipio_id}>
            <div className="relative">
              <select
                value={form.municipio_id}
                onChange={(e) => update('municipio_id', e.target.value)}
                className="w-full px-4 py-3 rounded-xl border border-gray-200 bg-white appearance-none focus:ring-2 focus:ring-purple-500/20 focus:border-purple-500"
                data-testid="coletivo-municipio-select"
              >
                <option value="">Selecione...</option>
                {municipios.map(m => <option key={m.id} value={m.id}>{m.nome}</option>)}
              </select>
              <MapPin className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
            </div>
          </FormField>

          <FormField label="Bairro / ponto de referência" hint="Onde o coletivo atua ou se encontra">
            <input
              type="text"
              value={form.bairro}
              onChange={(e) => update('bairro', e.target.value)}
              placeholder="Ex: Centro — Praça da Matriz"
              className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:ring-2 focus:ring-purple-500/20 focus:border-purple-500"
              data-testid="coletivo-bairro-input"
            />
          </FormField>

          <FormField label="Telefone/WhatsApp de contato" required error={errors.responsavel_contato}>
            <div className="relative">
              <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="tel"
                value={form.responsavel_contato}
                onChange={(e) => update('responsavel_contato', e.target.value)}
                placeholder="(31) 99999-9999"
                className="w-full pl-10 pr-4 py-3 rounded-xl border border-gray-200 focus:ring-2 focus:ring-purple-500/20 focus:border-purple-500"
                data-testid="coletivo-contato-input"
              />
            </div>
          </FormField>

          <div className="grid sm:grid-cols-2 gap-4">
            <FormField label="Instagram" hint="Opcional">
              <input
                type="text"
                value={form.instagram}
                onChange={(e) => update('instagram', e.target.value)}
                placeholder="@seucoletivo"
                className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:ring-2 focus:ring-purple-500/20 focus:border-purple-500"
                data-testid="coletivo-instagram-input"
              />
            </FormField>
            <FormField label="Facebook" hint="Opcional">
              <input
                type="text"
                value={form.facebook}
                onChange={(e) => update('facebook', e.target.value)}
                placeholder="facebook.com/seucoletivo"
                className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:ring-2 focus:ring-purple-500/20 focus:border-purple-500"
                data-testid="coletivo-facebook-input"
              />
            </FormField>
          </div>

          <button
            type="submit"
            disabled={submitting}
            className="w-full mt-2 py-3.5 bg-purple-600 text-white rounded-xl font-semibold hover:bg-purple-700 transition-colors disabled:opacity-60 flex items-center justify-center gap-2"
            data-testid="coletivo-submit-button"
          >
            {submitting && <Loader2 className="w-4 h-4 animate-spin" />}
            Enviar para análise
          </button>
          <p className="text-xs text-gray-500 text-center mt-3">
            Após a aprovação, você poderá adicionar fotos, gerenciar membros e criar eventos em "Meus Coletivos".
          </p>
        </form>
      </div>
    </div>
  );
}
